import type { MerchantModerationState, Restaurant } from '@noor/eats-api';
import { BRANCH_MODERATIONS, type Branch, type BranchModeration } from '../model/schema';

const BRANCH_MODERATION_BY_API_STATE = {
  MERCHANT_MODERATION_STATE_UNSPECIFIED: 'unspecified',
  MERCHANT_MODERATION_STATE_DRAFT: 'draft',
  MERCHANT_MODERATION_STATE_ON_MODERATION: 'pending',
  MERCHANT_MODERATION_STATE_APPROVED: 'approved',
  MERCHANT_MODERATION_STATE_REJECTED: 'rejected',
} as const satisfies Record<MerchantModerationState, BranchModeration>;

// Mapper between the merchant API's `v1Restaurant` and the branches screen's
// view model.

/**
 * Map one restaurant to a branch row.
 *
 * A branch only gets a `location` when its address carries real coordinates.
 * The API defaults missing `latitude`/`longitude` to `0`, and `(0, 0)` is a
 * REAL point in the Atlantic — passing it through would scatter pins off the
 * coast of Africa, so the pair is treated as "no coordinates" and the row
 * renders without a pin instead.
 */
export function toBranch(restaurant: Restaurant): Branch {
  const lat = restaurant.address?.latitude ?? 0;
  const long = restaurant.address?.longitude ?? 0;
  const hasLocation = lat !== 0 || long !== 0;
  const moderation = toBranchModeration(restaurant.moderation_state);

  return {
    id: restaurant.id,
    ...(restaurant.pickup_available === undefined
      ? {}
      : { pickupAvailable: restaurant.pickup_available }),
    ...(restaurant.is_popular === undefined ? {} : { isPopular: restaurant.is_popular }),
    ...(restaurant.accepting_orders_now === undefined
      ? {}
      : { acceptingOrdersNow: restaurant.accepting_orders_now }),
    name: restaurant.name,
    address: restaurant.address?.line ?? '',
    ...(hasLocation ? { location: { lat, long } } : {}),
    ...(moderation === undefined ? {} : { moderation }),
    ...(restaurant.rejection_reason?.trim()
      ? { rejectionReason: restaurant.rejection_reason.trim() }
      : {}),
    // Showcase detail for the view-mode drawer — empty strings/zeros are
    // "no data", not values, so they stay absent and the drawer falls back.
    ...(restaurant.icon ? { iconUrl: restaurant.icon } : {}),
    ...(restaurant.background_image
      ? { backgroundUrl: restaurant.background_image }
      : {}),
    ...(restaurant.free_delivery_threshold > 0
      ? { freeDeliveryThreshold: restaurant.free_delivery_threshold }
      : {}),
    ...(restaurant.menu_source ? { menuSource: restaurant.menu_source } : {}),
    ...(restaurant.description ? { description: restaurant.description } : {}),
    ...((restaurant.restaurant_category_ids ?? []).length > 0
      ? { categoryIds: [...(restaurant.restaurant_category_ids ?? [])] }
      : {}),
    ...(restaurant.working_hours?.open_time && restaurant.working_hours?.close_time
      ? {
          workingHours: {
            open: restaurant.working_hours.open_time,
            close: restaurant.working_hours.close_time,
          },
        }
      : {}),
  };
}

/**
 * `moderation_state` → the card's badge states.
 */
function toBranchModeration(
  moderationState: MerchantModerationState | undefined,
): BranchModeration | undefined {
  if (!moderationState) return undefined;
  const mapped = BRANCH_MODERATION_BY_API_STATE[moderationState];
  return mapped && BRANCH_MODERATIONS.includes(mapped) ? mapped : undefined;
}

/** Map the merchant's branch listing to the screen's rows, in API order. */
export function toBranches(restaurants: Restaurant[]): Branch[] {
  return restaurants.map(toBranch);
}
